import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import {
    ArrowLeft,
    Image as ImageIcon,
    ImageOff,
    ImagePlus,
    Images,
    UserRound,
    Users,
    Pencil,
    Plus,
    Printer,
    Search,
    Sparkles,
    Trash2,
    AlertOctagon,
    Save,
    X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { useRevalidateOnFocus } from "../lib/useRevalidateOnFocus";
import { characterAvatar } from "../lib/characterAvatar";
import { compareScenes } from "../lib/scenes";
import { entryThumbSrc } from "../lib/media";
import ProjectHeader from "../components/ProjectHeader";
import ContinuityBlock from "../components/ContinuityBlock";
import EditCharacterDialog from "../components/EditCharacterDialog";
import FlagButton from "../components/FlagButton";
import SupervisorNotesSection from "../components/SupervisorNotesSection";
import CharacterSceneAIBreakdown from "../components/CharacterSceneAIBreakdown";
import UnassignedAIBreakdownList from "../components/UnassignedAIBreakdownList";
import LookFilter from "../components/LookFilter";
import EpisodeFilter from "../components/EpisodeFilter";
import AllergyBadge, { pickCharacterAllergyNote } from "../components/AllergyBadge";
import { ALLERGY_NOTE_CHANGED_EVENT } from "../lib/allergy";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import usePolling from "../hooks/usePolling";
import { printCharacterContinuitySheet } from "../lib/printSheet";

/**
 * Character continuity view — Character → Scenes → (notes, images).
 *
 * Single GET to `/characters/:id/continuity` returns the character meta
 * plus one block per scene the character is in. Each block carries the
 * (character, scene) pair-specific notes and the image entries for that
 * pair — wired through the same shared `ContinuityBlock` component used
 * by SceneDetail so the UX is symmetric.
 */
export default function CharacterDetail() {
    const { projectId, characterId } = useParams();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const navigate = useNavigate();
    const location = useLocation();

    const incomingLens = location.state?.lens;
    const [project, setProject] = useState(null);
    const [character, setCharacter] = useState(null);
    const [blocks, setBlocks] = useState([]);
    // Iter-157J — Fittings (Unassigned) entries are character-level
    // prep photos with no scene_id. Rendered as a distinct block
    // above the scene continuity list so the H&M character-first
    // workflow has an obvious home.
    const [fittings, setFittings] = useState({ entries: [], entry_count: 0 });
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    // iter-56: inline edit dialog — keeps the user on the same character
    // detail view instead of routing them out to the Cast overview.
    const [editOpen, setEditOpen] = useState(false);
    // iter-75 PART 4 — Look filter state, scoped to this view.
    const [lookFilter, setLookFilter] = useState("");
    // iter-103 PART 1 — episode filter on Character detail. The
    // character's scene list is episode-aware (derived from each
    // scene's `episode` field); `null` = All.
    const [episode, setEpisode] = useState(null);
    // Iter-118.4 PART 2 — free-text search across the character's
    // scene appearances. Matches: scene_number, episode.scene
    // shorthand, scene heading/location, scene description, the
    // character's own name + number.
    // Iter-130a — look strings are now part of the haystack too, so
    // typing "Hero", "Fight", "10 Years Later" etc. lands the user
    // on the right scene without flipping between LookFilter chips.
    const [sceneQuery, setSceneQuery] = useState("");

    // Iter-130a — View mode toggle. "scenes" (default) shows every
    // scene the character is in even when empty (the existing flow
    // optimised for "what scenes am I in"). "gallery" hides scenes
    // with zero images and surfaces a continuity-first reading of
    // the same data — fast visual browsing of looks. Filters and
    // search still compose, so a user can stack Gallery + an episode
    // and a look chip together for a tight comparison view.
    //
    // Iter-158.13.2.20f.33b RESTORATION — .20f.33's Fittings Notes
    // insertion accidentally clobbered this declaration during a
    // search_replace, tripping `ReferenceError: viewMode is not
    // defined` and breaking Character Detail across the whole
    // deployed app (see Iter-20f.33b freeze-blocker regression
    // suite for the runtime lock).
    const [viewMode, setViewMode] = useState("scenes");

    // Iter-158.13.2.20f.33 — Character-level Fittings Notes.
    // Notes with `character_id === this && !scene_id` belong to
    // the Character (not to a Scene) and follow them across every
    // Scene continuity context. `characterNotes` holds the list;
    // `allergyNote` is the (at most one) note tagged "allergy"
    // used to drive the safety indicator. Data source is the
    // existing `/supervisor-notes` endpoint — no schema change.
    const [characterNotes, setCharacterNotes] = useState([]);
    const [notesLoaded, setNotesLoaded] = useState(false);
    const [notesBusy, setNotesBusy] = useState(false);
    const [notesError, setNotesError] = useState("");
    const [addForm, setAddForm] = useState({ open: false, kind: null, text: "", editingId: null });
    const allergyAnchorRef = useRef(null);
    const allergyNote = pickCharacterAllergyNote(characterNotes, characterId);

    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    const isContributor = project?.role === "contributor";
    // Iter-156A.5 Phase 5 — archive read-only enforcement on the FE.
    const isArchived = Boolean(project?.archived);
    // Iter-98 / Iter-156D — Contributor role can now add and edit
    // their own scene/character notes. Per-row Edit/Delete on the
    // list is additionally creator-or-admin scoped (mirrors the
    // backend ACL).
    const canManageNotes =
        (isOwner || isAdmin || isContributor) && !isArchived;
    // Iter-156D — admin-override for note row-actions + document
    // governance items inside SupervisorNotesSection. Contributors
    // can ADD notes (canManageNotes) but only Owner/Admin get the
    // edit/delete on OTHER PEOPLE'S notes and the Paste/Upload tools.
    const canAdminNotes = (isOwner || isAdmin) && !isArchived;
    // Iter-83 / Iter-107 — fine-grained Cast caps. Edit gates the pencil button;
    // backend enforces both independently. Admins resolve to all-true via
    // /permissions/me.
    const canEditCast =
        !isArchived &&
        (isOwner || isAdmin || Boolean(permissions?.cast_edit));
    const backHref = `${routeBase}/${projectId}/characters/${characterId}`;
    const backLabel = character?.name
        ? character.name.split(" ")[0]
        : "character";

    const loadContinuity = useCallback(async () => {
        try {
            const { data } = await api.get(
                `${apiBase}/${projectId}/characters/${characterId}/continuity`,
            );
            setCharacter(data.character);
            // Iter-157J — Fittings (Unassigned) bucket. Defensive
            // shape — older backends won't return this field; the
            // section then simply renders empty.
            setFittings(
                data.fittings && Array.isArray(data.fittings.entries)
                    ? data.fittings
                    : { entries: [], entry_count: 0 },
            );
            // Iter-129 — canonical episode + scene-number ordering.
            // The block list always has a `.scene` payload we can
            // sort on; null-scene entries (rare; defensive) sink.
            const sortedBlocks = [...(data.scenes || [])].sort((a, b) =>
                compareScenes(a?.scene || {}, b?.scene || {}),
            );
            setBlocks(sortedBlocks);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [projectId, characterId]);

    const loadProjectMeta = useCallback(async () => {
        try {
            const [{ data: p }, { data: perms }] = await Promise.all([
                api.get(`${apiBase}/${projectId}`),
                api.get(`${apiBase}/${projectId}/permissions/me`),
            ]);
            setProject(p);
            setPermissions(perms?.permissions || {});
        } catch (e) {
            setError(formatApiError(e));
        }
    }, [projectId]);

    useEffect(() => {
        loadProjectMeta();
    }, [loadProjectMeta]);

    // Iter-156E — focus-revalidation so out-of-band governance /
    // visibility changes propagate without manual refresh.
    useRevalidateOnFocus(loadProjectMeta);

    useEffect(() => {
        loadContinuity();
    }, [loadContinuity]);

    useEffect(() => {
        const onSaved = () => loadContinuity();
        window.addEventListener("onsetflow:state-entry-saved", onSaved);
        return () =>
            window.removeEventListener("onsetflow:state-entry-saved", onSaved);
    }, [loadContinuity]);

    // Iter-95 — background polling so updates from other devices appear
    // without a manual refresh.
    usePolling(loadContinuity, { intervalMs: 12000 });

    // Iter-158.13.2.20f.33 — Character-level notes fetch. Uses the
    // existing `/supervisor-notes` endpoint (returns all project
    // notes up to 2000, image_data stripped — matches Alex's
    // "reuse existing note storage / API" rule). Client-side
    // filters to notes attached to this Character and NOT to any
    // Scene. No new endpoint, no schema drift.
    const loadCharacterNotes = useCallback(async () => {
        if (!apiBase || !projectId || !characterId) return;
        try {
            const { data } = await api.get(`${apiBase}/${projectId}/supervisor-notes`);
            const notes = Array.isArray(data) ? data : [];
            const mine = notes.filter(
                (n) =>
                    n
                    && n.character_id === characterId
                    && !n.scene_id
                    && n.archived !== true,
            );
            setCharacterNotes(mine);
        } catch (e) {
            setNotesError(formatApiError(e));
        } finally {
            setNotesLoaded(true);
        }
    }, [apiBase, projectId, characterId]);

    useEffect(() => {
        loadCharacterNotes();
    }, [loadCharacterNotes]);

    // Iter-158.13.2.20f.33d — refresh characterNotes when a peer
    // surface (e.g. PrepMode → Cast → CharacterFittingsNotes) fires
    // ALLERGY_NOTE_CHANGED_EVENT after create/update/delete. Same
    // event CharacterDetail.saveCharacterNote already dispatches
    // out. Guards against navigating away and back to find the
    // safety indicator stale.
    useEffect(() => {
        const handler = () => loadCharacterNotes();
        window.addEventListener(ALLERGY_NOTE_CHANGED_EVENT, handler);
        return () =>
            window.removeEventListener(ALLERGY_NOTE_CHANGED_EVENT, handler);
    }, [loadCharacterNotes]);

    const openAddNote = () =>
        setAddForm({ open: true, kind: "note", text: "", editingId: null });
    const openAddAllergy = () =>
        setAddForm({ open: true, kind: "allergy", text: "", editingId: null });
    const openEditAllergy = () =>
        allergyNote
            ? setAddForm({
                  open: true,
                  kind: "allergy",
                  text: allergyNote.text || "",
                  editingId: allergyNote.id,
              })
            : openAddAllergy();
    const openEditNote = (note) =>
        setAddForm({
            open: true,
            kind: (note.tags || []).includes("allergy") ? "allergy" : "note",
            text: note.text || "",
            editingId: note.id,
        });
    const cancelAddNote = () =>
        setAddForm({ open: false, kind: null, text: "", editingId: null });

    // Iter-158.13.2.20f.33 — Save handler. POST for new, PATCH for
    // edit. Emits `onsetflow:allergy-note-changed` on any allergy
    // create/update/delete so other surfaces (SceneDetail,
    // BetaHMCharacters) refresh their indicator maps without a
    // full page reload.
    const saveCharacterNote = async () => {
        const trimmed = (addForm.text || "").trim();
        if (!trimmed) {
            setNotesError("Note text cannot be empty.");
            return;
        }
        setNotesBusy(true);
        setNotesError("");
        const isAllergy = addForm.kind === "allergy";
        const tags = isAllergy ? ["allergy"] : [];
        try {
            // Iter-158.13.2.20f.33d — capture the server response
            // (backend returns the created/updated doc) so we can
            // OPTIMISTICALLY update `characterNotes` in-place before
            // the rehydration GET completes. Human UAT observed the
            // safety indicator remaining stale after a successful
            // save until the user navigated away and back; the
            // rehydration round-trip was racing browser cache on
            // some devices. Optimistic update removes that race
            // altogether while preserving the rehydration as a
            // belt-and-braces read-your-writes.
            let saved = null;
            if (addForm.editingId) {
                const resp = await api.patch(
                    `${apiBase}/${projectId}/supervisor-notes/${addForm.editingId}`,
                    {
                        text: trimmed,
                        tags,
                        scene_id: null,
                        character_id: characterId,
                        visibility: "team",
                    },
                );
                saved = resp?.data || null;
            } else {
                const resp = await api.post(`${apiBase}/${projectId}/supervisor-notes`, {
                    text: trimmed,
                    tags,
                    scene_id: null,
                    character_id: characterId,
                    visibility: "team",
                });
                saved = resp?.data || null;
            }
            // Optimistic in-place merge — keeps the visible state
            // fresh for this render pass even before loadCharacterNotes
            // returns. Sourced from the server response so all fields
            // (id, created_at, created_by, tags) match what the read
            // API would return.
            if (saved && saved.id) {
                setCharacterNotes((prev) => {
                    const rest = prev.filter((n) => n && n.id !== saved.id);
                    return [saved, ...rest];
                });
            }
            cancelAddNote();
            await loadCharacterNotes();
            if (isAllergy) {
                window.dispatchEvent(new Event(ALLERGY_NOTE_CHANGED_EVENT));
            }
            toast.success(
                addForm.editingId
                    ? isAllergy ? "Allergy note updated" : "Note updated"
                    : isAllergy ? "Allergy note added" : "Note added",
            );
        } catch (e) {
            setNotesError(formatApiError(e));
        } finally {
            setNotesBusy(false);
        }
    };

    // Iter-158.13.2.20f.33 — Delete. Backend enforces "admin"
    // authority on DELETE `/supervisor-notes/{id}` (matches
    // `hmfull_delete_supervisor_note`) so the button is gated on
    // `canAdminNotes` here in addition. Any allergy deletion
    // broadcasts the same refresh event.
    const removeCharacterNote = async (note) => {
        if (!note || !note.id) return;
        // eslint-disable-next-line no-alert
        const ok = window.confirm(
            (note.tags || []).includes("allergy")
                ? "Remove this allergy note? The safety indicator will disappear from every Scene."
                : "Remove this note?",
        );
        if (!ok) return;
        setNotesBusy(true);
        setNotesError("");
        try {
            await api.delete(
                `${apiBase}/${projectId}/supervisor-notes/${note.id}`,
            );
            await loadCharacterNotes();
            if ((note.tags || []).includes("allergy")) {
                window.dispatchEvent(new Event(ALLERGY_NOTE_CHANGED_EVENT));
            }
            toast.success("Note removed");
        } catch (e) {
            setNotesError(formatApiError(e));
        } finally {
            setNotesBusy(false);
        }
    };

    // Iter-158.13.2.20f.33 — hash-scroll to the allergy note when
    // arriving via the safety indicator (`/characters/:id#allergy`).
    useEffect(() => {
        if (!notesLoaded) return;
        if (typeof window === "undefined") return;
        if (window.location.hash !== "#allergy") return;
        if (!allergyAnchorRef.current) return;
        try {
            allergyAnchorRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        } catch {
            /* jsdom / older browsers — no-op */
        }
    }, [notesLoaded, allergyNote]);


    const handleNotesChange = (sceneId, next) => {
        setBlocks((prev) =>
            prev.map((b) =>
                b.scene && b.scene.id === sceneId
                    ? {
                          ...b,
                          notes: next.notes,
                          notes_updated_at: next.notes_updated_at,
                          notes_updated_by_name: next.notes_updated_by_name,
                      }
                    : b,
            ),
        );
    };

    const triggerCapture = (scene) => (_action) => {
        // Iter-122 — camera capture disabled globally. Both ContinuityBlock
        // buttons now route to "photos" regardless of the legacy "camera"
        // action label so the upload picker is the single, consistent
        // path. Context still pre-fills mode + character + scene +
        // episode so the AssignmentDialog opens fully primed.
        window.dispatchEvent(
            new CustomEvent("onsetflow:capture", {
                detail: {
                    action: "photos",
                    context: {
                        mode: "onset",
                        scene_id: scene?.id || undefined,
                        character_id: characterId,
                        episode: scene?.episode || undefined,
                    },
                },
            }),
        );
    };

    // Iter-157J — Character-first Fittings upload. Fires the SAME
    // `onsetflow:capture` pipeline as every other upload surface so
    // there's only one code path under the hood. Pre-fills the
    // AssignmentDialog with mode="prep" + this character + NO scene,
    // so a Hair & Makeup user can save fitting photos before any
    // scene has been assigned. The user can still pick scenes
    // inside the dialog if they want; the default is "no scene".
    const triggerFittingUpload = () => {
        window.dispatchEvent(
            new CustomEvent("onsetflow:capture", {
                detail: {
                    action: "photos",
                    context: {
                        mode: "prep",
                        character_id: characterId,
                    },
                },
            }),
        );
    };

    const team = character?.team || "main";
    const totalImages = blocks.reduce(
        (sum, b) => sum + (b.entry_count || 0),
        0,
    );

    return (
        <div data-testid="character-detail-page" className="min-h-screen">
            <ProjectHeader
                projectName={project?.name}
                subtitle={character ? `Cast · ${character.name}` : "Cast"}
            />

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-48">
                {/* iter-74 PART 3 — honour an explicit returnTo when
                    we were opened from ImageDetail's tappable
                    Character link (or any future caller). Falls back
                    to the existing 'Back to Cast' lens-aware path
                    so deep links and Cast-row taps work as before. */}
                {(() => {
                    const returnTo = location.state?.returnTo;
                    if (returnTo?.path) {
                        return (
                            <button
                                type="button"
                                data-testid="character-back"
                                onClick={() =>
                                    navigate(returnTo.path, {
                                        state: location.state,
                                    })
                                }
                                className="tap inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white mb-4"
                            >
                                <ArrowLeft size={14} />
                                {returnTo.label || "Back"}
                            </button>
                        );
                    }
                    return (
                        <button
                            type="button"
                            data-testid="character-back"
                            onClick={() =>
                                navigate(`${routeBase}/${projectId}/cast`, {
                                    state: {
                                        lens:
                                            incomingLens ||
                                            (location.state?.lens ?? undefined),
                                    },
                                })
                            }
                            className="tap inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white mb-4"
                        >
                            <ArrowLeft size={14} />
                            Back to Cast
                        </button>
                    );
                })()}

                {error ? (
                    <p
                        data-testid="character-error"
                        className="text-sm text-red-400 mb-4"
                    >
                        {error}
                    </p>
                ) : null}

                {/* Character header card */}
                <section
                    data-testid="character-header"
                    className="rounded-xl border border-white/10 bg-card p-5 mb-5"
                >
                    <div className="flex items-start gap-4">
                        <div
                            data-testid="character-avatar"
                            className={`h-14 w-14 shrink-0 rounded-full font-display font-black flex items-center justify-center ${
                                team === "main"
                                    ? "bg-primary/20 text-primary"
                                    : "bg-amber-500/15 text-amber-200"
                            } ${
                                String(character?.character_number || "").trim().length >= 3
                                    ? "text-xl"
                                    : "text-2xl"
                            }`}
                        >
                            {characterAvatar(character)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                {/* iter-69 PART 4 — the inline `#N` chip
                                    was redundant: the avatar badge to
                                    the left already shows the assigned
                                    character number prominently. */}
                                <h1
                                    data-testid="character-name"
                                    className="font-display text-2xl font-bold text-white truncate"
                                >
                                    {character?.name || "—"}
                                </h1>
                                <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] border ${
                                        team === "main"
                                            ? "bg-primary/15 border-primary/40 text-primary"
                                            : "bg-amber-500/10 border-amber-500/30 text-amber-200"
                                    }`}
                                >
                                    {team === "main" ? (
                                        <UserRound size={10} />
                                    ) : (
                                        <Users size={10} />
                                    )}
                                    {team === "main" ? "Main" : "Crowd"}
                                </span>
                                {/* Iter-158.13.2.20f.33 — Allergy safety
                                    indicator. Rendered only when a
                                    Character-level allergy note exists.
                                    Click scrolls to the source note
                                    on this same page (already inside
                                    CharacterDetail, so no navigation
                                    needed). */}
                                <AllergyBadge
                                    note={allergyNote}
                                    testid="character-header-allergy-badge"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (allergyAnchorRef.current) {
                                            allergyAnchorRef.current.scrollIntoView({
                                                behavior: "smooth",
                                                block: "start",
                                            });
                                        }
                                    }}
                                />
                            </div>
                            {character?.actor ? (
                                <p
                                    data-testid="character-actor"
                                    className="text-sm text-neutral-400 mt-1"
                                >
                                    {character.actor}
                                </p>
                            ) : null}
                            {character?.notes ? (
                                /* iter-69 PART 4 — surface the
                                   description / notes so HoDs see
                                   important character context at a
                                   glance, not only inside Edit. */
                                <p
                                    data-testid="character-notes"
                                    className="text-[13px] text-neutral-400 mt-1.5 leading-relaxed whitespace-pre-wrap break-words"
                                >
                                    {character.notes}
                                </p>
                            ) : null}
                            <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-2 flex items-center gap-1.5 flex-wrap">
                                <span>
                                    {blocks.filter((b) => b.scene).length} scene
                                    {blocks.filter((b) => b.scene).length === 1
                                        ? ""
                                        : "s"}
                                </span>
                                <span aria-hidden="true">·</span>
                                {/* Iter-130a — clickable image count.
                                    Tapping the count flips this view
                                    into Gallery mode (only scenes with
                                    photos). Tapping again returns to
                                    the full scene list. */}
                                <button
                                    type="button"
                                    data-testid="character-image-count"
                                    onClick={() =>
                                        setViewMode((v) =>
                                            v === "gallery" ? "scenes" : "gallery",
                                        )
                                    }
                                    aria-pressed={viewMode === "gallery"}
                                    title={
                                        viewMode === "gallery"
                                            ? "Showing only scenes with photos. Tap to show all scenes."
                                            : "Tap to view scenes with photos only (Gallery)."
                                    }
                                    className={`tap inline-flex items-center gap-1 rounded-full px-2 -mx-2 text-[11px] uppercase tracking-[0.2em] transition-colors ${
                                        viewMode === "gallery"
                                            ? "text-primary hover:text-primary/80"
                                            : "text-neutral-300 hover:text-white"
                                    }`}
                                >
                                    {totalImages} image
                                    {totalImages === 1 ? "" : "s"}
                                    {viewMode === "gallery" ? (
                                        <span className="ml-1 text-[9px] tracking-normal normal-case font-semibold">
                                            · gallery
                                        </span>
                                    ) : null}
                                </button>
                                {/* Iter-154 — deep-link into the global
                                    Gallery view filtered to this
                                    character. Sits inline next to the
                                    existing per-page Gallery toggle so
                                    the workflows don't fight: in-page
                                    toggle hides scenes-without-photos
                                    here; this link sends the user to
                                    the cross-project visual browser. */}
                                {totalImages > 0 ? (
                                    <>
                                        <span aria-hidden="true">·</span>
                                        <button
                                            type="button"
                                            data-testid="character-open-gallery"
                                            onClick={() =>
                                                navigate(
                                                    `${routeBase}/${projectId}/gallery?character=${characterId}`,
                                                )
                                            }
                                            title="Open Gallery — visual continuity browser for this character"
                                            className="tap inline-flex items-center gap-1 rounded-full px-2 -mx-2 text-[11px] uppercase tracking-[0.2em] text-neutral-300 hover:text-white"
                                        >
                                            <Images size={11} />
                                            Open Gallery
                                        </button>
                                    </>
                                ) : null}
                            </p>
                        </div>
                        <div className="shrink-0 flex items-center gap-1.5">
                            {character?.id ? (
                                <FlagButton
                                    projectId={projectId}
                                    entityType="character"
                                    entityId={character.id}
                                    entityLabel={character.name || "character"}
                                    testid="character-detail-flag"
                                    size="lg"
                                />
                            ) : null}
                            <button
                                type="button"
                                data-testid="character-print-continuity"
                                onClick={() =>
                                    printCharacterContinuitySheet({
                                        productionName: project?.name,
                                        character,
                                        blocks,
                                        fittings,
                                        projectId,
                                        apiBase,
                                    })
                                }
                                title="Print A4 continuity sheet for this character"
                                className="tap h-9 w-9 rounded-md text-neutral-400 hover:text-white hover:bg-white/5 inline-flex items-center justify-center"
                            >
                                <Printer size={14} />
                            </button>
                            {canEditCast ? (
                                <button
                                    type="button"
                                    data-testid="character-edit"
                                    onClick={() => setEditOpen(true)}
                                    title="Edit character"
                                    className="tap h-9 w-9 rounded-md text-neutral-400 hover:text-white hover:bg-white/5 inline-flex items-center justify-center"
                                >
                                    <Pencil size={14} />
                                </button>
                            ) : null}
                        </div>
                    </div>
                </section>

                {/* Iter-158.13.2.20f.33e — the Fittings-photo
                    upload/unassigned-media workspace is intentionally
                    NOT rendered on the general Cast → Character
                    surface. Human UAT: this block does not belong on
                    a Character page that a crew member reaches
                    outside the Fittings workflow. The equivalent
                    upload + unassigned-media surface remains on the
                    actual Fittings workflow inside PrepMode → Cast →
                    selected Character (via the existing capture
                    pipeline) and every stored Fittings photograph
                    remains untouched — this repair is presentation-
                    only. Underlying `data.fittings` payload from
                    `/characters/{id}/continuity` and the entire
                    upload code path are preserved so re-enabling
                    the surface (or exposing it on a different
                    route) is a one-line JSX change if ever needed.
                    See Iter-158.13.2.20f.33e regression suite for
                    the mounted-route lock. */}
                {/* Iter-158.13.2.20f.33e — Fittings-photo workspace
                    NOT rendered on general Cast → Character (see
                    comment above). Force-gated to `false` so a
                    future re-enable is a one-line flip; underlying
                    state (`fittings`), the upload trigger and the
                    Fittings entry links all remain wired via the
                    shared capture pipeline. */}
                {false && !loading ? (
                    <section
                        data-testid="character-fittings-section"
                        className="mb-5"
                    >
                        {fittings.entry_count > 0 ? (
                            <div
                                data-testid="character-fittings-block"
                                className="rounded-xl border border-white/10 bg-card overflow-hidden"
                            >
                                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
                                    <div className="h-9 w-9 shrink-0 rounded-full bg-amber-500/15 text-amber-200 flex items-center justify-center">
                                        <Sparkles size={14} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-white font-semibold leading-tight">
                                            Fittings
                                            <span className="ml-1.5 text-[10px] uppercase tracking-[0.18em] text-amber-200/80">
                                                Unassigned
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-neutral-500 mt-0.5">
                                            {fittings.entry_count} photo
                                            {fittings.entry_count === 1 ? "" : "s"}
                                            {" · "}
                                            not yet attached to a scene
                                        </div>
                                    </div>
                                    {canManageNotes ? (
                                        <button
                                            type="button"
                                            data-testid="character-fittings-upload"
                                            onClick={triggerFittingUpload}
                                            title="Upload fitting photos for this character"
                                            aria-label="Upload fitting photos"
                                            className="shrink-0 tap inline-flex items-center justify-center h-9 w-9 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                                        >
                                            <ImagePlus size={14} />
                                        </button>
                                    ) : null}
                                </div>
                                <ul
                                    data-testid="character-fittings-grid"
                                    className="grid grid-cols-3 sm:grid-cols-4 gap-2 p-4"
                                >
                                    {fittings.entries.map((e) => {
                                        const thumb = entryThumbSrc(projectId, e, apiBase);
                                        return (
                                            <li key={e.id}>
                                                <Link
                                                    to={`${routeBase}/${projectId}/state-entries/${e.id}`}
                                                    state={{ backHref, backLabel }}
                                                    data-testid={`character-fittings-entry-${e.id}`}
                                                    className="block relative aspect-square rounded-md overflow-hidden bg-black border border-white/10 hover:border-white/30 transition-colors"
                                                >
                                                    {thumb ? (
                                                        <img
                                                            src={thumb}
                                                            alt={e.note || "fitting"}
                                                            loading="lazy"
                                                            className="absolute inset-0 w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="absolute inset-0 flex items-center justify-center text-neutral-600">
                                                            <ImageOff size={18} />
                                                        </div>
                                                    )}
                                                    {e.look ? (
                                                        <span
                                                            className="absolute left-1 right-1 bottom-1 px-1.5 py-0.5 rounded-md bg-black/65 backdrop-blur-sm text-[10px] text-white truncate"
                                                            title={e.look}
                                                        >
                                                            {e.look}
                                                        </span>
                                                    ) : null}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ) : canManageNotes ? (
                            // Empty state — extra-prominent when the
                            // user is in the Fittings/Prep lens so a
                            // first-time Hair & Makeup user instantly
                            // sees where to add fitting photos. When
                            // accessed from On Set we still render the
                            // entry point but at calm priority so it
                            // doesn't shout at users who are mid-shoot.
                            <button
                                type="button"
                                data-testid="character-fittings-empty-upload"
                                onClick={triggerFittingUpload}
                                className={`tap w-full text-left rounded-xl border bg-card transition-colors ${
                                    incomingLens === "prep"
                                        ? "border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 p-5"
                                        : "border-dashed border-white/10 hover:border-white/20 hover:bg-white/[0.02] p-4"
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div
                                        className={`h-11 w-11 shrink-0 rounded-full flex items-center justify-center ${
                                            incomingLens === "prep"
                                                ? "bg-amber-400/20 text-amber-200"
                                                : "bg-secondary text-neutral-300"
                                        }`}
                                    >
                                        <ImagePlus size={18} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div
                                            className={`font-semibold leading-tight ${
                                                incomingLens === "prep"
                                                    ? "text-white text-base"
                                                    : "text-neutral-200 text-sm"
                                            }`}
                                        >
                                            Upload Fitting Photos
                                        </div>
                                        <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">
                                            No scene needed — add fitting / prep
                                            captures for{" "}
                                            <span className="text-neutral-200">
                                                {character?.name || "this character"}
                                            </span>
                                            . You can attach them to scenes later.
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ) : null}
                    </section>
                ) : null}

                {/* Iter-158.13.2.20f.33c — CharacterDetail is a
                    Scene/Character continuity working view, not the
                    Fittings workflow. Human UAT on .20f.33 caught
                    ORDINARY Fittings Notes leaking into this surface
                    because the whole notes list was rendered here
                    verbatim. Alex's governing distinction:
                      - Ordinary Note: available from Fittings only
                        (PrepMode → Cast → selected Character panel
                        via CharacterFittingsNotes). Does NOT follow
                        the Character into On Set / Scene continuity
                        working views.
                      - Allergy: safety information — follows the
                        Character into relevant H&M working contexts
                        (this view included).
                    Repair: this surface is now allergy-only. The
                    ordinary-note list, Note CTA and "Fittings Notes"
                    heading are removed. Allergy row + Add/Edit
                    allergy are preserved so the CharacterDetail
                    surface remains the source of truth the safety
                    badge can navigate to. Underlying data model,
                    endpoints and CharacterFittingsNotes component
                    are untouched. */}
                {!loading && canManageNotes ? (
                    <section
                        data-testid="character-allergy-section"
                        className="mb-5 rounded-xl border border-white/10 bg-card overflow-hidden"
                    >
                        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
                            <div
                                data-testid="character-allergy-heading"
                                className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-semibold flex-1"
                            >
                                Allergy
                            </div>
                            {allergyNote ? (
                                <button
                                    type="button"
                                    data-testid="character-fittings-notes-allergy-edit"
                                    onClick={openEditAllergy}
                                    className="tap inline-flex items-center gap-1 h-7 px-2 rounded-md border border-red-400/40 bg-red-500/10 text-red-200 text-[11px] font-semibold hover:bg-red-500/20"
                                >
                                    <AlertOctagon size={11} /> Edit allergy
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    data-testid="character-fittings-notes-allergy-add"
                                    onClick={openAddAllergy}
                                    className="tap inline-flex items-center gap-1 h-7 px-2 rounded-md border border-red-400/40 bg-red-500/10 text-red-200 text-[11px] font-semibold hover:bg-red-500/20"
                                >
                                    <AlertOctagon size={11} /> Add allergy
                                </button>
                            )}
                        </div>
                        {addForm.open && addForm.kind === "allergy" ? (
                            <div
                                data-testid="character-fittings-notes-form"
                                className="px-4 py-3 border-b border-white/5 bg-white/[0.02]"
                            >
                                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">
                                    {addForm.editingId ? "Edit allergy" : "Allergy · free text"}
                                </div>
                                <Textarea
                                    data-testid="character-fittings-notes-form-text"
                                    value={addForm.text}
                                    onChange={(e) =>
                                        setAddForm((f) => ({ ...f, text: e.target.value }))
                                    }
                                    placeholder="e.g. Latex + adhesive sensitivity. Use hypoallergenic mastic only."
                                    rows={3}
                                    className="text-sm"
                                />
                                {notesError ? (
                                    <div
                                        data-testid="character-fittings-notes-form-error"
                                        className="mt-1.5 text-[11px] text-red-300"
                                    >
                                        {notesError}
                                    </div>
                                ) : null}
                                <div className="mt-2 flex items-center justify-end gap-2">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        data-testid="character-fittings-notes-form-cancel"
                                        onClick={cancelAddNote}
                                        disabled={notesBusy}
                                    >
                                        <XIcon size={12} /> Cancel
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        data-testid="character-fittings-notes-form-save"
                                        onClick={saveCharacterNote}
                                        disabled={notesBusy}
                                    >
                                        <Save size={12} /> Save
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                        {allergyNote ? (
                            <div
                                data-testid="character-allergy-row"
                                ref={allergyAnchorRef}
                                id="allergy"
                                className="px-4 py-3 flex items-start gap-3"
                            >
                                <span className="mt-0.5 shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-red-400/40 bg-red-500/15 text-red-200 text-[9px] uppercase tracking-[0.15em] font-semibold">
                                    <AlertOctagon size={10} /> Allergy
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div
                                        data-testid={`character-fittings-notes-item-text-${allergyNote.id}`}
                                        className="text-sm text-neutral-100 whitespace-pre-wrap break-words"
                                    >
                                        {allergyNote.text}
                                    </div>
                                    {allergyNote.created_by_name ? (
                                        <div className="text-[10px] text-neutral-500 mt-1">
                                            {allergyNote.created_by_name}
                                        </div>
                                    ) : null}
                                </div>
                                {canAdminNotes ? (
                                    <button
                                        type="button"
                                        data-testid={`character-fittings-notes-remove-${allergyNote.id}`}
                                        onClick={() => removeCharacterNote(allergyNote)}
                                        aria-label="Remove allergy"
                                        title="Remove (admin only)"
                                        className="tap inline-flex items-center justify-center h-7 w-7 rounded-md text-red-300 hover:bg-red-500/10"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                ) : null}
                            </div>
                        ) : notesLoaded && !addForm.open ? (
                            <div
                                data-testid="character-allergy-empty"
                                className="px-4 py-6 text-center text-[11px] text-neutral-500"
                            >
                                No allergy recorded.
                            </div>
                        ) : null}
                    </section>
                ) : null}

                {/* Per-scene continuity blocks */}
                {/* Iter-130a — subtext explains current view + gives a
                    one-tap exit when Gallery hides scenes the user
                    was expecting to see. Keeps the surface calm —
                    just an explanation line, no new chrome. */}
                <p
                    data-testid="character-scenes-subtext"
                    className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-2 flex items-center gap-2"
                >
                    {viewMode === "gallery" ? (
                        <>
                            <ImageIcon size={11} className="text-primary" />
                            Gallery · scenes with photos
                            <button
                                type="button"
                                data-testid="character-gallery-exit"
                                onClick={() => setViewMode("scenes")}
                                className="ml-1 tap text-[10px] tracking-normal normal-case underline text-neutral-400 hover:text-white"
                            >
                                show all scenes
                            </button>
                        </>
                    ) : (
                        "Scenes this character appears in"
                    )}
                </p>
                {loading ? (
                    <div
                        data-testid="character-loading"
                        className="space-y-3"
                    >
                        {[0, 1].map((i) => (
                            <div
                                key={i}
                                className="h-44 rounded-xl bg-card border border-white/10 animate-pulse"
                            />
                        ))}
                    </div>
                ) : blocks.length === 0 ? (
                    <div
                        data-testid="character-empty"
                        className="rounded-xl border border-dashed border-white/10 p-8 text-center"
                    >
                        <ImageIcon size={22} className="mx-auto text-neutral-500" />
                        <p className="mt-3 text-sm text-neutral-300">
                            <span className="text-white font-medium">
                                {character?.name || "This character"}
                            </span>{" "}
                            isn&rsquo;t in any scene yet.
                        </p>
                        <p className="text-xs text-neutral-500 mt-1">
                            Attach them to a scene from the scene detail page,
                            then come back here to see continuity per scene.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* iter-75 PART 4 — collect every distinct Look
                            from the loaded entries and offer a chip
                            rail so the user can isolate one Look at a
                            glance. Hidden when there's no Look on
                            file or only a single value. */}
                        {(() => {
                            const allLooks = [];
                            for (const b of blocks) {
                                for (const e of b.entries || []) {
                                    if (e?.look) allLooks.push(e.look);
                                }
                            }
                            // iter-103 PART 1 — episode options derived
                            // straight from the character's scene list
                            // so the filter never offers an episode with
                            // zero scenes for this character.
                            const episodeOptions = Array.from(
                                new Set(
                                    (blocks || [])
                                        .map((b) => b?.scene?.episode)
                                        .filter(Boolean)
                                        .map(String),
                                ),
                            ).sort((a, b) => {
                                const ai = parseInt(a, 10);
                                const bi = parseInt(b, 10);
                                if (!Number.isNaN(ai) && !Number.isNaN(bi)) return ai - bi;
                                if (!Number.isNaN(ai)) return -1;
                                if (!Number.isNaN(bi)) return 1;
                                return a.localeCompare(b);
                            });
                            return (
                                <>
                                    <EpisodeFilter
                                        testid="character-episode-filter"
                                        episodes={episodeOptions}
                                        value={episode}
                                        onChange={setEpisode}
                                    />
                                    {/* Iter-118.4 PART 2 — scene search.
                                        Composes with the episode filter:
                                        when an episode is picked we only
                                        search inside it; "All" episodes
                                        searches across the whole list.
                                        Matches scene number ("39"),
                                        episode.scene ("1.39"), heading,
                                        description, and the character's
                                        own name / number — so a quick
                                        "hallway" or "39" lands the user
                                        on the right scene without
                                        scrolling a long list. */}
                                    <div className="relative mb-3" data-testid="character-scene-search-wrap">
                                        <Search
                                            size={14}
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                                        />
                                        <Input
                                            data-testid="character-scene-search"
                                            value={sceneQuery}
                                            onChange={(e) => setSceneQuery(e.target.value)}
                                            placeholder="Search scenes (e.g. 39, 1.39, hallway…)"
                                            className="pl-8 h-10 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                        />
                                        {sceneQuery ? (
                                            <button
                                                type="button"
                                                data-testid="character-scene-search-clear"
                                                onClick={() => setSceneQuery("")}
                                                aria-label="Clear search"
                                                className="instant-tap absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-white hover:bg-white/10"
                                            >
                                                <span className="text-base leading-none">×</span>
                                            </button>
                                        ) : null}
                                    </div>
                                    <LookFilter
                                        looks={allLooks}
                                        value={lookFilter}
                                        onChange={setLookFilter}
                                        testidPrefix="character-look-filter"
                                        className="mb-4"
                                    />
                                </>
                            );
                        })()}
                        {/* Iter-118.4 PART 2 — empty-search hint. Tells
                            the user when their query yielded zero
                            scenes inside the current episode subset
                            instead of leaving them staring at a blank
                            section. Only renders when search is ON
                            AND no block survives the predicates. */}
                        {(() => {
                            if (!sceneQuery.trim()) return null;
                            const q = sceneQuery.trim().toLowerCase();
                            const visible = blocks.filter((b) => {
                                const scene = b.scene;
                                if (!scene) return false;
                                if (episode && String(scene.episode || "") !== String(episode)) {
                                    return false;
                                }
                                // Iter-130a — same Gallery gate as the
                                // main loop below: search empty-state
                                // must respect Gallery mode.
                                if (viewMode === "gallery" && !(b.entry_count > 0)) {
                                    return false;
                                }
                                const sn = String(scene.scene_number || "").toLowerCase();
                                const ep = String(scene.episode || "");
                                const epScene = ep ? `${ep}.${sn}`.toLowerCase() : "";
                                // Iter-130a — include look strings so
                                // typing "hero", "fight", "10 years
                                // later" lands the right scene without
                                // flipping between Look chips.
                                const looks = (b.entries || [])
                                    .map((e) => (e?.look || "").toLowerCase())
                                    .filter(Boolean)
                                    .join(" ");
                                const hay = [
                                    sn,
                                    epScene,
                                    (scene.heading || "").toLowerCase(),
                                    (scene.description || "").toLowerCase(),
                                    (scene.int_ext || "").toLowerCase(),
                                    (scene.time_of_day || "").toLowerCase(),
                                    (character?.name || "").toLowerCase(),
                                    String(character?.character_number || "").toLowerCase(),
                                    looks,
                                ].join(" ");
                                return hay.includes(q);
                            }).length;
                            if (visible > 0) return null;
                            return (
                                <p
                                    data-testid="character-scene-search-empty"
                                    className="text-sm text-neutral-500 text-center py-6 rounded-xl border border-dashed border-white/10 mb-4"
                                >
                                    No scenes match &ldquo;{sceneQuery}&rdquo;
                                    {episode ? ` in Ep ${episode}` : ""}.{" "}
                                    <button
                                        type="button"
                                        onClick={() => setSceneQuery("")}
                                        className="underline hover:text-white"
                                    >
                                        Clear search
                                    </button>
                                </p>
                            );
                        })()}
                        {/* Iter-130a — Gallery empty state. When the
                            character has zero captured images, Gallery
                            mode would otherwise render an empty list.
                            Tell the user what happened + offer the
                            one-tap return. */}
                        {viewMode === "gallery" &&
                        blocks.every((b) => !(b.entry_count > 0)) ? (
                            <p
                                data-testid="character-gallery-empty"
                                className="text-sm text-neutral-400 text-center py-8 rounded-xl border border-dashed border-white/10 mb-4"
                            >
                                <ImageIcon
                                    size={16}
                                    className="mx-auto text-neutral-500 mb-2"
                                />
                                No photos yet for{" "}
                                <span className="text-white font-medium">
                                    {character?.name || "this character"}
                                </span>
                                . Upload from any scene below to start the
                                gallery.
                                <button
                                    type="button"
                                    onClick={() => setViewMode("scenes")}
                                    data-testid="character-gallery-empty-exit"
                                    className="block mt-2 mx-auto underline hover:text-white"
                                >
                                    Show all scenes
                                </button>
                            </p>
                        ) : null}
                        <ul
                            data-testid="character-blocks-list"
                            className="space-y-4"
                        >
                            {blocks.map((b) => {
                                const scene = b.scene;
                                if (!scene) return null;
                                // Iter-130a — Gallery mode: hide scenes
                                // with zero captured images so the
                                // user can scroll a tight visual feed.
                                if (viewMode === "gallery" && !(b.entry_count > 0)) {
                                    return null;
                                }
                                // iter-103 PART 1 — apply episode filter
                                // first so the user only sees scenes from
                                // the selected episode.
                                if (episode && String(scene.episode || "") !== String(episode)) {
                                    return null;
                                }
                                // Iter-118.4 PART 2 — search filter.
                                // Composed AFTER the episode filter so
                                // queries respect the episode subset.
                                // Matches scene_number, "ep.scene"
                                // shorthand, heading, description, the
                                // character's name + number AND look
                                // strings (iter-130a).
                                if (sceneQuery.trim()) {
                                    const q = sceneQuery.trim().toLowerCase();
                                    const sn = String(scene.scene_number || "").toLowerCase();
                                    const ep = String(scene.episode || "");
                                    const epScene = ep ? `${ep}.${sn}`.toLowerCase() : "";
                                    const looks = (b.entries || [])
                                        .map((e) => (e?.look || "").toLowerCase())
                                        .filter(Boolean)
                                        .join(" ");
                                    const hay = [
                                        sn,
                                        epScene,
                                        (scene.heading || "").toLowerCase(),
                                        (scene.description || "").toLowerCase(),
                                        (scene.int_ext || "").toLowerCase(),
                                        (scene.time_of_day || "").toLowerCase(),
                                        (character?.name || "").toLowerCase(),
                                        String(character?.character_number || "").toLowerCase(),
                                        looks,
                                    ].join(" \u0001 ");
                                    if (!hay.includes(q)) return null;
                                }
                                const filteredEntries = lookFilter
                                    ? (b.entries || []).filter(
                                          (e) =>
                                              (e.look || "").toLowerCase() ===
                                              lookFilter.toLowerCase(),
                                      )
                                    : b.entries;
                                // When a filter is active and a block
                                // has no matching entries, hide it
                                // entirely so the user only sees what
                                // they asked for.
                                if (lookFilter && (!filteredEntries || filteredEntries.length === 0)) {
                                    return null;
                                }
                                const sceneNumber = scene.scene_number || "—";
                                // Iter-102 PART 2 — episode-prefixed scene
                                // label. Two episodes can both have a "7"
                                // so "Sc 7" alone is ambiguous in TV
                                // projects; render "Sc 1.7" / "Sc 4.7"
                                // when the scene has an episode. The
                                // underlying `to` link uses the scene's
                                // UUID so navigation always lands on the
                                // correct scene regardless of display.
                                const sceneLabel = scene.episode
                                    ? `Sc ${scene.episode}.${sceneNumber}`
                                    : `Sc ${sceneNumber}`;
                                const avatarLabel = scene.episode
                                    ? `${scene.episode}.${sceneNumber}`
                                    : sceneNumber;
                                return (
                                    <li key={scene.id}>
                                    <ContinuityBlock
                                        projectId={projectId}
                                        sceneId={scene.id}
                                        characterId={characterId}
                                        canEditNotes={canEditCast}
                                        scene={scene}
                                        header={{
                                            kind: "scene",
                                            avatar: avatarLabel,
                                            primary: (
                                                <span className="flex items-center gap-2">
                                                    <span className="truncate">
                                                        {sceneLabel}
                                                    </span>
                                                    {scene.int_ext ? (
                                                        <span className="text-[9px] uppercase tracking-[0.15em] text-neutral-200 bg-secondary border border-white/10 rounded-full px-1.5">
                                                            {scene.int_ext}
                                                        </span>
                                                    ) : null}
                                                    {scene.time_of_day ? (
                                                        <span className="text-[9px] uppercase tracking-[0.15em] text-neutral-200 bg-secondary border border-white/10 rounded-full px-1.5">
                                                            {scene.time_of_day}
                                                        </span>
                                                    ) : null}
                                                    {/* iter-73 PART 2 — surface
                                                        the "not in script"
                                                        flag so HoDs know
                                                        these photos exist
                                                        outside the scripted
                                                        cast list (e.g. last-
                                                        minute extras). */}
                                                    {b.not_in_script ? (
                                                        <span
                                                            data-testid={`character-block-not-in-script-${scene.id}`}
                                                            className="text-[9px] uppercase tracking-[0.15em] text-amber-200 bg-amber-400/15 border border-amber-400/30 rounded-full px-1.5"
                                                        >
                                                            Not in script
                                                        </span>
                                                    ) : null}
                                                </span>
                                            ),
                                            secondary: (
                                                <>
                                                    {scene.heading
                                                        ? `${scene.heading} · `
                                                        : ""}
                                                    {b.entry_count} image
                                                    {b.entry_count === 1
                                                        ? ""
                                                        : "s"}
                                                </>
                                            ),
                                            to: `${routeBase}/${projectId}/scenes/${scene.id}`,
                                        }}
                                        notes={b.notes}
                                        notesUpdatedAt={b.notes_updated_at}
                                        notesUpdatedByName={
                                            b.notes_updated_by_name
                                        }
                                        entries={filteredEntries}
                                        backHref={backHref}
                                        backLabel={backLabel}
                                        // Iter-114c — re-introduced
                                        // capture+upload on Character
                                        // Detail per the new spec. The
                                        // iter-52 view-only carve-out
                                        // forced users back to the scene
                                        // page just to add a photo. Now
                                        // both Capture and Upload sit in
                                        // the block header with the full
                                        // context (project, episode,
                                        // scene, character) auto-filled.
                                        onCapture={triggerCapture(scene)}
                                        onNotesChange={(next) =>
                                            handleNotesChange(scene.id, next)
                                        }
                                    />
                                    {/* Iter-158.13.2.20f.42-relational — the
                                        AI Hair & Makeup Breakdown records
                                        related to BOTH this Character AND
                                        this Scene live directly under the
                                        Scene's continuity surface. Same
                                        physical hm_breakdown_entries
                                        documents surfaced in Bulk Edit
                                        Scenes and Scenes → Scene → Character
                                        — one record, three presentations. */}
                                    <CharacterSceneAIBreakdown
                                        apiBase={apiBase}
                                        projectId={projectId}
                                        sceneId={scene.id}
                                        characterId={characterId}
                                        testidPrefix="character-scene-ai"
                                    />
                                </li>
                            );
                        })}
                        </ul>
                    </>
                )}

                {/* iter-52 #4: Capture is intentionally NOT exposed on
                    Cast → Character. Capture must originate from a Scene
                    context (Scene Detail / Scenes Hub) or via the
                    bottom capture bar on Fittings/On Set surfaces. */}

                <SupervisorNotesSection
                    projectId={projectId}
                    characterId={characterId}
                    isOwner={canManageNotes}
                    canAdmin={canAdminNotes}
                    showCharacterRef={false}
                    testid="character-supervisor-notes"
                    emptyHint="No notes for this character yet."
                />

                {/* Iter-158.13.2.20f.42-relational — the Character-
                    level H&M Working Breakdown mount was replaced.
                    Scene-related findings for this Character now
                    live under each Scene's own AI Hair & Makeup
                    Breakdown accordion (above), so a character-only
                    DeptBreakdownSection would have duplicated those
                    rows here. This section carries ONLY findings
                    whose scene_ids array is empty — the truly
                    Character-wide leftovers — filtered client-side
                    from the SAME hm_breakdown_entries records. No
                    new endpoint. No second CRUD. No writes on
                    toggle. Default collapsed; whole section omitted
                    when no genuinely unassigned rows exist. */}
                <UnassignedAIBreakdownList
                    apiBase={apiBase}
                    projectId={projectId}
                    mode="character"
                    targetId={characterId}
                    testidPrefix="character-wide-ai"
                />
            </main>

            {/* iter-56: inline edit — replaces the previous bug where
                tapping the pencil punted users to the Cast overview. */}
            <EditCharacterDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                projectId={projectId}
                character={character}
                onSaved={(updated) => {
                    setCharacter((prev) => ({ ...(prev || {}), ...updated }));
                }}
            />
        </div>
    );
}
